/**
 * Admin Audit-Log Page — visualizes the activity_log MongoDB collection.
 * Backed by GET /api/activity (admin-only). Supports filter by user, action,
 * limit, and CSV export. Compliance-friendly (GDPR / B2B audits).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Activity, Download, Filter, RefreshCw, Loader2, ShieldAlert, User,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { formatDateTimeCH } from "@/lib/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLOR = {
  status_change: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  login: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  logout: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  create: "bg-green-500/15 text-green-300 border-green-500/30",
  delete: "bg-red-500/15 text-red-300 border-red-500/30",
  update: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  publish: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  approval: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};
const actionBadgeClass = (a) => {
  const key = Object.keys(ACTION_COLOR).find((k) => (a || "").toLowerCase().includes(k));
  return ACTION_COLOR[key] || "bg-white/10 text-white/70 border-white/15";
};

export default function ActivityLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [limit, setLimit] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (filterUser.trim()) params.user_id = filterUser.trim();
      if (filterAction && filterAction !== "all") params.action = filterAction;
      const { data } = await axios.get(`${API}/activity`, { params, withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.status === 403
        ? "Admin-Rechte erforderlich"
        : "Audit-Log konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const distinctActions = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.action && s.add(i.action));
    return Array.from(s).sort();
  }, [items]);

  const downloadCsv = () => {
    const header = ["Zeitpunkt", "Aktion", "Benutzer", "Email", "Details"];
    const rows = items.map((i) => [
      formatDateTimeCH(i.timestamp),
      i.action || "",
      i.actor?.name || i.user_id || "",
      i.actor?.email || "",
      (i.details || "").replace(/"/g, '""'),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportiert");
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="activity-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[var(--studio-text)] flex items-center gap-2">
            <Activity className="w-7 h-7 text-[var(--studio-accent)]" />
            Audit-Log
          </h1>
          <p className="text-sm text-[var(--studio-text-muted)] mt-1">
            Alle Systemaktionen mit Zeitstempel, Benutzer und Details. DSGVO- und Compliance-konform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="activity-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button onClick={downloadCsv} disabled={!items.length} data-testid="activity-export-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
        </div>
      </div>

      <PageHelp pageKey="activity-log" title="So nutzt du das Audit-Log">
        <div className="space-y-2">
          <p>Das Audit-Log protokolliert alle sicherheitsrelevanten Aktionen: Logins, Statusaenderungen, Veroeffentlichungen, Rechteanpassungen, Loeschungen.</p>
          <p><strong>Filter:</strong> Nach Benutzer (User-ID), Aktion oder Anzahl Eintraege.</p>
          <p><strong>Export:</strong> CSV mit UTF-8 BOM (Excel-kompatibel), Datum im Schweizer Format.</p>
          <p>Das System behaelt die letzten 100 Eintraege automatisch — fuer laengere Retention bitte regelmaessig exportieren.</p>
        </div>
      </PageHelp>

      <Card className="mb-4 border-[var(--studio-border)] bg-[var(--studio-surface)]">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--studio-text-muted)] flex items-center gap-1">
              <User className="w-3 h-3" /> Benutzer-ID
            </Label>
            <Input
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              placeholder="user_xxx (leer = alle)"
              className="mt-2 bg-[var(--studio-bg)] border-[var(--studio-border)]"
              data-testid="activity-filter-user"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--studio-text-muted)] flex items-center gap-1">
              <Filter className="w-3 h-3" /> Aktion
            </Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="mt-2 bg-[var(--studio-bg)] border-[var(--studio-border)]" data-testid="activity-filter-action">
                <SelectValue placeholder="Alle Aktionen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Aktionen</SelectItem>
                {distinctActions.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--studio-text-muted)]">
              Anzahl Eintraege
            </Label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
              <SelectTrigger className="mt-2 bg-[var(--studio-bg)] border-[var(--studio-border)]" data-testid="activity-filter-limit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100, 200].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} Eintraege</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={load} className="w-full" data-testid="activity-apply-btn">
              <Filter className="w-4 h-4 mr-2" /> Filter anwenden
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--studio-border)] bg-[var(--studio-surface)]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            {items.length} Eintraege
          </CardTitle>
          {!loading && items.length === 0 && (
            <Badge variant="outline" className="text-xs">Keine Treffer</Badge>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--studio-accent)]" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-[var(--studio-text-muted)] text-sm flex flex-col items-center gap-2">
              <ShieldAlert className="w-8 h-8 opacity-50" />
              <p>Keine Eintraege fuer die aktuellen Filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activity-table">
                <thead className="text-xs uppercase tracking-wider text-[var(--studio-text-muted)]">
                  <tr className="border-b border-[var(--studio-border)]">
                    <th className="text-left py-2 pr-3">Zeitpunkt</th>
                    <th className="text-left py-2 pr-3">Aktion</th>
                    <th className="text-left py-2 pr-3">Benutzer</th>
                    <th className="text-left py-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id || `${i.user_id}-${i.timestamp}`} className="border-b border-[var(--studio-border)]/40 hover:bg-white/5">
                      <td className="py-2 pr-3 text-[var(--studio-text-muted)] whitespace-nowrap">
                        {formatDateTimeCH(i.timestamp)}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className={`text-[10px] ${actionBadgeClass(i.action)}`}>
                          {i.action || "—"}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-col">
                          <span className="text-[var(--studio-text)]">
                            {i.actor?.name || i.actor?.email || i.user_id || "—"}
                          </span>
                          {i.actor?.email && i.actor?.name && (
                            <span className="text-[10px] text-[var(--studio-text-muted)]">
                              {i.actor.email}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-[var(--studio-text-muted)]">
                        {i.details || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-[var(--studio-text-muted)] mt-4">
        Hinweis: Das System speichert automatisch die letzten 100 Eintraege. Fuer langfristige Audits bitte regelmaessig CSV-Export erstellen.
      </p>
    </div>
  );
}
